/**
 * Conformance Score Value Object - Following GTD Pattern
 * Represents the quality metrics for artifact conformance to meta-models and design
 */
export class ConformanceScore {
  constructor(
    public readonly metaModelScore: number,
    public readonly designCoverageScore: number,
    public readonly confidence: 'high' | 'medium' | 'low',
    public readonly calculatedAt: Date = new Date()
  ) {
    this.validateScore(metaModelScore, 'metaModelScore');
    this.validateScore(designCoverageScore, 'designCoverageScore');
  }

  private validateScore(score: number, field: string): void {
    if (score < 0 || score > 100) {
      throw new Error(`${field} must be between 0 and 100. Got: ${score}`);
    }
  }

  get overallScore(): number {
    return Math.round((this.metaModelScore + this.designCoverageScore) / 2);
  }

  isPassingGrade(): boolean {
    return this.overallScore >= 80 && this.metaModelScore >= 70 && this.designCoverageScore >= 70;
  }

  needsImprovement(): boolean {
    return this.overallScore < 80 || this.metaModelScore < 70 || this.designCoverageScore < 70;
  }

  static create(metaModelScore: number, designCoverageScore: number, confidence: 'high' | 'medium' | 'low'): ConformanceScore {
    return new ConformanceScore(metaModelScore, designCoverageScore, confidence);
  }

  static zero(): ConformanceScore {
    return new ConformanceScore(0, 0, 'low');
  }

  equals(other: ConformanceScore): boolean {
    return this.metaModelScore === other.metaModelScore &&
           this.designCoverageScore === other.designCoverageScore &&
           this.confidence === other.confidence;
  }

  toString(): string {
    return `${this.overallScore}% (Meta: ${this.metaModelScore}%, Design: ${this.designCoverageScore}%) - ${this.confidence} confidence`;
  }
}